import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, Wifi, MapPin, Settings, CheckCircle, Package, ArrowRight } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { createHotspot, type CreateHotspotData } from "@/api/services/hotspotService";
import apiClient from "@/api/client";
import ENDPOINTS from "@/api/endpoints";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const MapContainerAny = MapContainer as unknown as React.ComponentType<Record<string, unknown>>;
const TileLayerAny = TileLayer as unknown as React.ComponentType<Record<string, unknown>>;
const MarkerAny = Marker as unknown as React.ComponentType<Record<string, unknown>>;

interface CreateHotspotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (hotspot: Record<string, unknown>) => void;
}

interface HotspotFormData {
  name: string;
  ssid: string;
  location: {
    latitude: number;
    longitude: number;
    address: string;
    city: string;
    state: string;
    country: string;
  };
  bandwidthLimit: number;
  maxConnections: number;
  hardware: {
    model: string;
    macAddress: string;
  };
  // Telecom-grade additive fields (all optional)
  nodeType?: string;
  installationType?: string;
  vendor?: string;
  serialNumber?: string;
  internetType?: string;
  antennaType?: string;
}

interface PendingRegistration {
  orderId: string;
  orderNumber: string;
  itemIndex: number;
  productId: string;
  productName: string;
  thumbnailUrl: string | null;
  totalSlots: number;
  slotsAvailable: number;
  purchasedAt: string;
}


// Fix Leaflet's default marker icon paths (bundlers drop the images otherwise).
const _markerIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

interface NominatimResult {
  display_name: string;
  lat: string;
  lon: string;
  address?: Record<string, string>;
}

// Small map with a draggable pin. Calls onMove(lat,lng) when the pin is dragged
// or the map is clicked.
function LocationPickerMap({
  lat,
  lng,
  onMove,
}: {
  lat: number;
  lng: number;
  onMove: (lat: number, lng: number) => void;
}) {
  function ClickCapture() {
    useMapEvents({
      click(e) {
        onMove(e.latlng.lat, e.latlng.lng);
      },
    });
    return null;
  }
  // Fly the map to new coordinates whenever they change. MapContainer only
  // honours `center` on first mount, so we drive it imperatively here.
  function Recenter({ lat, lng }: { lat: number; lng: number }) {
    const map = useMap();
    useEffect(() => {
      if (Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)) {
        map.setView([lat, lng], 16, { animate: true });
      }
    }, [lat, lng, map]);
    return null;
  }
  const hasPos = Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0);
  const center: [number, number] = hasPos ? [lat, lng] : [9.082, 8.6753]; // Nigeria centroid fallback
  return (
    <div className="overflow-hidden rounded-lg ring-1 ring-white/10" style={{ height: 180 }}>
      <MapContainerAny
        center={center}
        zoom={hasPos ? 15 : 6}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={false}
      >
        <TileLayerAny
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; OpenStreetMap contributors'
        />
        <ClickCapture />
        <Recenter lat={lat} lng={lng} />
        {hasPos && (
          <MarkerAny
            position={[lat, lng]}
            icon={_markerIcon}
            draggable={true}
            eventHandlers={{
              dragend: (e: L.DragEndEvent) => {
                const p = e.target.getLatLng();
                onMove(p.lat, p.lng);
              },
            }}
          />
        )}
      </MapContainerAny>
    </div>
  );
}

export default function CreateHotspotModal({ isOpen, onClose, onSuccess }: CreateHotspotModalProps) {
  const { theme } = useApp();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState(1);

  // Gate state: pending KonectX Store hotspot purchases for this operator.
  const [pendingItems, setPendingItems] = useState<PendingRegistration[] | null>(null);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingError, setPendingError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<PendingRegistration | null>(null);

  const [formData, setFormData] = useState<HotspotFormData>({
    name: "",
    ssid: "",
    location: { latitude: 0, longitude: 0, address: "", city: "", state: "", country: "" },
    bandwidthLimit: 100,
    maxConnections: 50,
    hardware: { model: "", macAddress: "" },
    nodeType: "Standard Hotspot",
    installationType: "",
    vendor: "",
    serialNumber: "",
    internetType: "",
    antennaType: "",
  });

  // Load (or reload) the operator's unregistered purchased units.
  const loadPending = async (opts?: { keepForm?: boolean }) => {
    setPendingError(null);
    try {
      setPendingLoading(true);
      const res = await apiClient.get("/hotspots/pending-registrations");
      const data = res?.data?.pending || [];
      setPendingItems(data);

      if (data.length === 0) {
        setSelectedItem(null);
      } else if (data.length === 1) {
        // Exactly one unit left -> skip the picker, pre-fill, go to the form.
        setSelectedItem(data[0]);
        if (!opts?.keepForm) {
          setFormData((prev) => ({ ...prev, name: data[0].productName || prev.name }));
        }
      } else {
        // Multiple units -> show the picker, clear any prior selection.
        setSelectedItem(null);
      }
      return data;
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setPendingError(err.response?.data?.message || "Could not load your purchases. Try again.");
      setPendingItems([]);
      return [];
    } finally {
      setPendingLoading(false);
    }
  };

  // Fetch pending registrations when the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setPendingItems(null);
    setSelectedItem(null);
    setPendingError(null);
    setStep(1);
    setSuccess(false);
    setError(null);
    loadPending();
  }, [isOpen]);

  const updateField = (path: string, value: string | number) => {
    setFormData((prev) => {
      const keys = path.split(".");
      if (keys.length === 1) {
        return { ...prev, [keys[0]]: value };
      }
      const [parent, child] = keys as [keyof HotspotFormData, string];
      const parentObj = prev[parent] as unknown as Record<string, unknown>;
      return {
        ...prev,
        [parent]: { ...parentObj, [child]: value },
      };
    });
  };

  // Track which fields have been interacted with, so we only show errors
  // after the user has touched a field (avoids yelling on a fresh form).
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (key: string) => setTouched((t) => ({ ...t, [key]: true }));

  // ── Address autocomplete (OpenStreetMap Nominatim, free, no key) ─────────
  const [addrQuery, setAddrQuery] = useState("");
  const [addrResults, setAddrResults] = useState<NominatimResult[]>([]);
  const [addrSearching, setAddrSearching] = useState(false);

  const searchAddress = async () => {
    const q = addrQuery.trim();
    if (q.length < 3) return;
    try {
      setAddrSearching(true);
      const url =
        "https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&q=" +
        encodeURIComponent(q);
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      const data: NominatimResult[] = await res.json();
      const list = Array.isArray(data) ? data : [];
      if (list.length > 0) {
        // Auto-apply the best match immediately: fills fields + moves the pin.
        pickAddressInternal(list[0], false);
        // Still show the list so the user can choose a different match.
        setAddrResults(list);
        setAddrQuery(q);
      } else {
        setAddrResults([]);
      }
    } catch {
      setAddrResults([]);
    } finally {
      setAddrSearching(false);
    }
  };

  const pickAddressInternal = (r: NominatimResult, closeList: boolean) => {
    const a = r.address || {};
    const lat = parseFloat(r.lat);
    const lon = parseFloat(r.lon);
    setFormData((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        address: r.display_name,
        city: a.city || a.town || a.village || a.county || prev.location.city,
        state: a.state || a.region || prev.location.state,
        country: a.country || prev.location.country,
        latitude: Number.isFinite(lat) ? lat : prev.location.latitude,
        longitude: Number.isFinite(lon) ? lon : prev.location.longitude,
      },
    }));
    if (closeList) setAddrResults([]);
    setAddrQuery(r.display_name);
    setTouched((t) => ({ ...t, address: true, city: true, country: true }));
  };

  // Picking from the dropdown closes the list.
  const pickAddress = (r: NominatimResult) => pickAddressInternal(r, true);

  const setPin = (lat: number, lng: number) => {
    setFormData((prev) => ({
      ...prev,
      location: { ...prev.location, latitude: lat, longitude: lng },
    }));
  };


  // Auto-format a MAC address as the user types.
  // Strips non-hex, uppercases, inserts ':' every 2 chars, caps at 6 octets.
  const formatMac = (raw: string) => {
    const hex = (raw || "")
      .toUpperCase()
      .replace(/[^0-9A-F]/g, "")
      .slice(0, 12);
    return hex.match(/.{1,2}/g)?.join(":") ?? "";
  };

  const isValidMac = (mac: string) => /^([0-9A-F]{2}:){5}[0-9A-F]{2}$/.test(mac || "");

  // Per-step validity. Used to gate Next / Register.
  const isStepValid = (s: number) => {
    if (s === 1) {
      return (
        formData.name.trim().length > 0 &&
        formData.ssid.trim().length > 0 &&
        Number(formData.bandwidthLimit) > 0 &&
        Number(formData.maxConnections) > 0
      );
    }
    if (s === 2) {
      return (
        formData.location.address.trim().length > 0 &&
        formData.location.city.trim().length > 0 &&
        formData.location.country.trim().length > 0
      );
    }
    if (s === 3) {
      return isValidMac(formData.hardware.macAddress);
    }
    return true;
  };

  const allValid = isStepValid(1) && isStepValid(2) && isStepValid(3);

  const resetForm = () => {
    setFormData({
      name: selectedItem?.productName || "",
      ssid: "",
      location: { latitude: 0, longitude: 0, address: "", city: "", state: "", country: "" },
      bandwidthLimit: 100,
      maxConnections: 50,
      hardware: { model: "", macAddress: "" },
      nodeType: "Standard Hotspot",
      installationType: "",
      vendor: "",
      serialNumber: "",
      internetType: "",
      antennaType: "",
    });
    setStep(1);
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    setSelectedItem(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedItem) {
      setError("Please select a purchased hotspot first.");
      return;
    }
    // Mark everything touched so any missing fields light up.
    setTouched({
      name: true, ssid: true, bandwidthLimit: true, maxConnections: true,
      address: true, city: true, country: true, macAddress: true,
    });
    if (!allValid) {
      setError("Please complete all required fields before registering.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        ...formData,
        orderId: selectedItem.orderId,
        orderItemIndex: selectedItem.itemIndex,
      };
      const result = await createHotspot(payload);
      onSuccess?.(result as unknown as Record<string, unknown>);

      // Reset the form for the next unit, then reload remaining units.
      setStep(1);
      setSelectedItem(null);
      setFormData({
        name: "",
        ssid: "",
        location: { latitude: 0, longitude: 0, address: "", city: "", state: "", country: "" },
        bandwidthLimit: 100,
        maxConnections: 50,
        hardware: { model: "", macAddress: "" },
      });

      const remaining = await loadPending();
      if (!remaining || remaining.length === 0) {
        // Nothing left -> show success then close.
        setSuccess(true);
        setTimeout(() => handleClose(), 1500);
      }
      // If units remain, loadPending() has already routed us to the picker
      // (2+) or pre-filled the single remaining unit (1).
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err.response?.data?.message || "Failed to register hotspot. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // ── Render: still loading the gate check ────────────────────────────────
  if (pendingLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="rounded-xl bg-[#1f2023] px-8 py-10 text-center text-gray-200">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
          Checking your KonectX purchases...
        </div>
      </div>
    );
  }

  // ── Render: gate screen (no pending purchases) ──────────────────────────
  if (pendingItems !== null && pendingItems.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="w-full max-w-lg rounded-xl bg-[#1f2023] p-6 text-gray-100">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold">{t('operator.hotspot.registerModal.title', { defaultValue: 'Register a Hotspot' })}</h2>
            <button onClick={handleClose} className="rounded p-1 text-gray-400 hover:bg-white/10">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="mb-5 flex items-center gap-3 rounded-lg bg-blue-500/10 p-4 text-sm text-blue-200">
            <Package className="h-5 w-5 shrink-0" />
            <div>
              <div className="font-medium">{t('operator.hotspot.registerModal.noPending', { defaultValue: 'No KonectX hotspots waiting to register' })}</div>
              <div className="mt-1 text-blue-200/80">
                {t('operator.hotspot.registerModal.storeOnlyInfo', { defaultValue: 'This option is only for hotspots you bought from the KonectX Store.' })}
              </div>
            </div>
          </div>

          <p className="mb-5 text-sm text-gray-300">
            {t('operator.hotspot.registerModal.byoInfo', { defaultValue: 'To register hardware you already own (Bring Your Own), go to Kit (BYO) in the sidebar. An activation fee applies.' })}
          </p>

          {pendingError && (
            <div className="mb-4 rounded bg-red-500/10 px-3 py-2 text-sm text-red-300">
              {pendingError}
            </div>
          )}

          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              onClick={() => { handleClose(); window.location.href = "/operator/store"; }}
              className="flex-1 rounded-lg bg-white/10 px-4 py-2 text-sm font-medium text-white hover:bg-white/20"
            >
              {t('operator.hotspot.registerModal.browseStoreBtn', { defaultValue: 'Browse Store' })}
            </button>
            <button
              onClick={() => { handleClose(); window.location.href = "/operator/operatorkit"; }}
              className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 inline-flex items-center justify-center gap-1.5"
            >
              {t('operator.hotspot.registerModal.registerOwnBtn', { defaultValue: 'Register Own Hardware' })} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: picker if more than one pending purchase ────────────────────
  if (pendingItems && pendingItems.length > 1 && !selectedItem) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="w-full max-w-xl rounded-xl bg-[#1f2023] p-6 text-gray-100">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold">Which hotspot are you registering?</h2>
            <button onClick={handleClose} className="rounded p-1 text-gray-400 hover:bg-white/10">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {pendingItems.map((p, idx) => (
              <button
                key={`${p.orderId}-${p.itemIndex}-${idx}`}
                onClick={() => {
                  setSelectedItem(p);
                  setFormData((prev) => ({ ...prev, name: p.productName || prev.name }));
                }}
                className="flex w-full items-center gap-3 rounded-lg border border-white/10 bg-[#2a2c30] p-3 text-left hover:border-blue-500 hover:bg-[#33363a]"
              >
                <Package className="h-6 w-6 shrink-0 text-blue-400" />
                <div className="flex-1">
                  <div className="font-medium">{p.productName}</div>
                  <div className="mt-0.5 text-xs text-gray-400">
                    {(p as PendingRegistration & { unitLabel?: string }).unitLabel ? (p as PendingRegistration & { unitLabel?: string }).unitLabel + " · " : ""}Order #{p.orderNumber}
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-500" />
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Render: success state ───────────────────────────────────────────────
  if (success) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="rounded-xl bg-[#1f2023] p-8 text-center text-gray-100">
          <CheckCircle className="mx-auto mb-3 h-12 w-12 text-green-400" />
          <div className="text-lg font-semibold">Hotspot registered</div>
          <div className="mt-1 text-sm text-gray-400">Pending verification — first user login confirms it.</div>
        </div>
      </div>
    );
  }

  // ── Render: form (selected item ready) ──────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-2xl rounded-xl bg-[#1f2023] p-6 text-gray-100 max-h-[85vh] overflow-y-auto">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Register Hotspot</h2>
          <button onClick={handleClose} className="rounded p-1 text-gray-400 hover:bg-white/10">
            <X className="h-5 w-5" />
          </button>
        </div>

        {selectedItem && (
          <div className="mb-5 rounded-lg bg-green-500/10 px-4 py-3 text-sm text-green-200">
            ✓ Registering: <span className="font-semibold">{selectedItem.productName}</span>
            {" — "}no activation fee (paid in Order #{selectedItem.orderNumber}).
          </div>
        )}

        {/* Step indicator */}
        <div className="mb-6 flex items-center gap-2">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex flex-1 items-center gap-2">
              <div className={`flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold ${step >= n ? "bg-blue-600 text-white" : "bg-white/10 text-gray-400"}`}>{n}</div>
              {n < 3 && <div className={`h-0.5 flex-1 ${step > n ? "bg-blue-600" : "bg-white/10"}`} />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-blue-400"><Wifi className="h-5 w-5" /><span className="font-medium">Basic Information</span></div>
            <div>
              <label className="mb-1 block text-sm text-gray-300">Hotspot Name *</label>
              <input
                value={formData.name}
                onChange={(e) => updateField("name", e.target.value)}
                onBlur={() => markTouched("name")}
                className="w-full rounded-lg bg-[#2a2c30] px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-blue-500"
                placeholder="e.g., Downtown Hub"
              />
              {touched.name && formData.name.trim().length === 0 && (
                <p className="mt-1 text-xs text-red-400">Hotspot name is required.</p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-300">SSID (Network Name) *</label>
              <input
                value={formData.ssid}
                onChange={(e) => updateField("ssid", e.target.value)}
                onBlur={() => markTouched("ssid")}
                className="w-full rounded-lg bg-[#2a2c30] px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-blue-500"
                placeholder="e.g., MyWifi_001"
              />
              {touched.ssid && formData.ssid.trim().length === 0 && (
                <p className="mt-1 text-xs text-red-400">SSID is required.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm text-gray-300">Bandwidth Limit (Mbps) *</label>
                <input
                  type="number"
                  value={formData.bandwidthLimit}
                  onChange={(e) => updateField("bandwidthLimit", Number(e.target.value))}
                  className="w-full rounded-lg bg-[#2a2c30] px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-300">Max Connections *</label>
                <input
                  type="number"
                  value={formData.maxConnections}
                  onChange={(e) => updateField("maxConnections", Number(e.target.value))}
                  className="w-full rounded-lg bg-[#2a2c30] px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-300">Node Type</label>
              <select
                value={formData.nodeType}
                onChange={(e) => updateField("nodeType", e.target.value)}
                className="w-full rounded-lg bg-[#2a2c30] px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-blue-500"
              >
                <option>Standard Hotspot</option>
                <option>Relay Node</option>
                <option>Core Node</option>
                <option>Premium Coverage Node</option>
              </select>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-blue-400"><MapPin className="h-5 w-5" /><span className="font-medium">Location</span></div>

            {/* Address search (OpenStreetMap, free) */}
            <div className="relative">
              <label className="mb-1 block text-sm text-gray-300">Search installation address</label>
              <div className="flex gap-2">
                <input
                  value={addrQuery}
                  onChange={(e) => setAddrQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchAddress(); } }}
                  placeholder="Start typing an address, then Search"
                  className="w-full rounded-lg bg-[#2a2c30] px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-blue-500"
                />
                <button type="button" onClick={searchAddress} className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-500 whitespace-nowrap">
                  {addrSearching ? "..." : "Search"}
                </button>
              </div>
              {addrResults.length > 0 && (
                <div className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-lg bg-[#1f2125] ring-1 ring-white/10 shadow-lg">
                  {addrResults.map((r, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => pickAddress(r)}
                      className="block w-full px-3 py-2 text-left text-xs text-gray-200 hover:bg-white/10"
                    >
                      {r.display_name}
                    </button>
                  ))}
                </div>
              )}
              <p className="mt-1 text-xs text-gray-500">Pick a result to auto-fill the fields, then drag the pin to fine-tune.</p>
            </div>

            {/* Map with draggable pin */}
            <LocationPickerMap
              lat={formData.location.latitude}
              lng={formData.location.longitude}
              onMove={setPin}
            />

            <div>
              <input value={formData.location.address} onChange={(e) => updateField("location.address", e.target.value)} onBlur={() => markTouched("address")} placeholder="Street address" className="w-full rounded-lg bg-[#2a2c30] px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-blue-500" />
              {touched.address && formData.location.address.trim().length === 0 && (
                <p className="mt-1 text-xs text-red-400">Address is required.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <input value={formData.location.city} onChange={(e) => updateField("location.city", e.target.value)} onBlur={() => markTouched("city")} placeholder="City" className="w-full rounded-lg bg-[#2a2c30] px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-blue-500" />
                {touched.city && formData.location.city.trim().length === 0 && (
                  <p className="mt-1 text-xs text-red-400">City is required.</p>
                )}
              </div>
              <input value={formData.location.state} onChange={(e) => updateField("location.state", e.target.value)} placeholder="State / Region" className="rounded-lg bg-[#2a2c30] px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-blue-500" />
            </div>
            <div>
              <input value={formData.location.country} onChange={(e) => updateField("location.country", e.target.value)} onBlur={() => markTouched("country")} placeholder="Country" className="w-full rounded-lg bg-[#2a2c30] px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-blue-500" />
              {touched.country && formData.location.country.trim().length === 0 && (
                <p className="mt-1 text-xs text-red-400">Country is required.</p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-300">Installation Type</label>
              <select
                value={formData.installationType}
                onChange={(e) => updateField("installationType", e.target.value)}
                className="w-full rounded-lg bg-[#2a2c30] px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-blue-500"
              >
                <option value="">Select installation type</option>
                <option>Indoor</option>
                <option>Outdoor</option>
                <option>Rooftop</option>
                <option>Tower</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="number" step="0.000001" value={formData.location.latitude} onChange={(e) => updateField("location.latitude", Number(e.target.value))} placeholder="Latitude" className="rounded-lg bg-[#2a2c30] px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-blue-500" />
              <input type="number" step="0.000001" value={formData.location.longitude} onChange={(e) => updateField("location.longitude", Number(e.target.value))} placeholder="Longitude" className="rounded-lg bg-[#2a2c30] px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-blue-500" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-blue-400"><Settings className="h-5 w-5" /><span className="font-medium">Hardware</span></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm text-gray-300">Vendor</label>
                <select
                  value={formData.vendor}
                  onChange={(e) => updateField("vendor", e.target.value)}
                  className="w-full rounded-lg bg-[#2a2c30] px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-blue-500"
                >
                  <option value="">Select vendor</option>
                  <option>MikroTik</option>
                  <option>TP-Link</option>
                  <option>Ubiquiti</option>
                  <option>GL.iNet</option>
                  <option>KuWFi</option>
                  <option>Custom</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-300">Device Model</label>
                <input value={formData.hardware.model} onChange={(e) => updateField("hardware.model", e.target.value)} placeholder="e.g., EAP225" className="w-full rounded-lg bg-[#2a2c30] px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-blue-500" />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-300">Serial Number</label>
              <input value={formData.serialNumber} onChange={(e) => updateField("serialNumber", e.target.value)} placeholder="Device serial number" className="w-full rounded-lg bg-[#2a2c30] px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-blue-500" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm text-gray-300">Internet Source</label>
                <select
                  value={formData.internetType}
                  onChange={(e) => updateField("internetType", e.target.value)}
                  className="w-full rounded-lg bg-[#2a2c30] px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-blue-500"
                >
                  <option value="">Select source</option>
                  <option>Fiber</option>
                  <option>4G</option>
                  <option>5G</option>
                  <option>Starlink</option>
                  <option>DSL</option>
                  <option>Fixed Wireless</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-300">Antenna</label>
                <select
                  value={formData.antennaType}
                  onChange={(e) => updateField("antennaType", e.target.value)}
                  className="w-full rounded-lg bg-[#2a2c30] px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-blue-500"
                >
                  <option value="">Select antenna</option>
                  <option>Omni-directional</option>
                  <option>Sector</option>
                  <option>Panel</option>
                  <option>Indoor</option>
                  <option>Built-in</option>
                </select>
              </div>
            </div>
            <input value={formData.hardware.macAddress} onChange={(e) => updateField("hardware.macAddress", formatMac(e.target.value))}
              onBlur={() => markTouched("macAddress")} placeholder="MAC address (XX:XX:XX:XX:XX:XX)" className="w-full rounded-lg bg-[#2a2c30] px-3 py-2 text-sm text-white outline-none ring-1 ring-white/10 focus:ring-blue-500 font-mono" />
            {touched.macAddress && !isValidMac(formData.hardware.macAddress) && (
              <p className="text-xs text-red-400">Enter a complete MAC address (e.g. 22:33:44:55:66:77).</p>
            )}
            <div className="text-xs text-gray-500">MAC address from the device label. Just type the digits — colons are added automatically.</div>
          </div>
        )}

        {error && <div className="mt-4 rounded bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</div>}

        <div className="mt-6 flex items-center justify-between">
          <button onClick={handleClose} className="text-sm text-gray-400 hover:text-white">Cancel</button>
          <div className="flex gap-2">
            {step > 1 && (
              <button onClick={() => setStep((s) => s - 1)} className="rounded-lg bg-white/10 px-4 py-2 text-sm hover:bg-white/20">Back</button>
            )}
            {step < 3 && (
              <button
                onClick={() => {
                  if (!isStepValid(step)) {
                    // Light up the current step's fields.
                    if (step === 1) setTouched((t) => ({ ...t, name: true, ssid: true }));
                    if (step === 2) setTouched((t) => ({ ...t, address: true, city: true, country: true }));
                    return;
                  }
                  setStep((s) => s + 1);
                }}
                disabled={!isStepValid(step)}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            )}
            {step === 3 && (
              <button
                onClick={handleSubmit}
                disabled={loading || !selectedItem || !allValid}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
              >
                {loading ? "Registering..." : "Register Hotspot"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
